import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Upload, message, Switch, Typography, Popconfirm } from 'antd';
import { PlusOutlined, UploadOutlined, DeleteOutlined } from '@ant-design/icons';
import { api, API_URL } from '../../utils/api';

const { Title } = Typography;

const QRManagement = () => {
  const [qrs, setQrs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const fetchQRs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/qrcodes');
      setQrs(response.data);
    } catch (error) {
      message.error('Failed to fetch QR codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQRs();
  }, []);

  const handleAdd = async (values) => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      
      // With normFile, values.image is the fileList array
      if (values.image && values.image.length > 0) {
        formData.append('image', values.image[0].originFileObj);
      } else {
        message.error('Please upload an image');
        setSubmitting(false);
        return;
      }
      formData.append('isActive', values.isActive || false);

      await api.post('/qrcodes', formData);
      message.success('QR Code added successfully');
      setIsModalVisible(false);
      form.resetFields();
      fetchQRs();
    } catch (error) {
      console.error('Add QR Error:', error);
      const msg = error.response?.data?.message || error.message || 'Failed to add QR Code';
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/qrcodes/${id}`);
      message.success('QR Code deleted successfully');
      fetchQRs();
    } catch (error) {
      message.error('Failed to delete QR Code');
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await api.put(`/qrcodes/${id}/toggle`);
      message.success('QR Code status updated');
      fetchQRs();
    } catch (error) {
      message.error('Failed to update status');
    }
  };

  const columns = [
    {
      title: 'Image',
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      render: (text) => (
        <img 
          src={`${API_URL}${text}`} 
          alt="QR" 
          style={{ width: 50, height: 50, objectFit: 'cover' }} 
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive, record) => (
        <Switch 
          checked={isActive} 
          onChange={() => handleToggleActive(record._id)} 
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Popconfirm
          title="Delete QR Code"
          description="Are you sure you want to delete this QR code?"
          onConfirm={() => handleDelete(record._id)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="link" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
        <Title level={2}>QR Management</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setIsModalVisible(true)}>
          Add QR Code
        </Button>
      </div>

      <Table 
        columns={columns} 
        dataSource={qrs} 
        rowKey="_id" 
        loading={loading}
      />

      <Modal
        title="Add QR Code"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAdd}
          initialValues={{ isActive: false }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="e.g. PhonePe Store QR" />
          </Form.Item>

          <Form.Item
            name="image"
            label="QR Image"
            rules={[{ required: true, message: 'Please upload an image' }]}
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <Upload 
              listType="picture" 
              beforeUpload={() => false} 
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Click to Upload</Button>
            </Upload>
          </Form.Item>

          <Form.Item name="isActive" label="Set as Active" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={submitting} block>
              Submit
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default QRManagement;
